import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Building2, Plus, KeyRound, Copy, PhoneForwarded, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "rounded-md border border-white/[0.1] bg-[#0B0D10] px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-[#F25F22]/60";

export default function CustomersPanel({ token }) {
  const [customers, setCustomers] = useState(null);
  const [form, setForm] = useState({ company_name: "", trade: "hvac", contact_email: "", oncall_phone: "", review_link: "" });
  const [saving, setSaving] = useState(false);
  const [portalFor, setPortalFor] = useState(null);
  const [portalEmail, setPortalEmail] = useState("");
  const [portalResult, setPortalResult] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [provisionBusy, setProvisionBusy] = useState(null);
  const [reviewFor, setReviewFor] = useState(null);
  const [reviewLink, setReviewLink] = useState("");

  const headers = { Authorization: `Bearer ${token}` };
  const load = () =>
    axios.get(`${API}/customers`, { headers }).then((r) => setCustomers(r.data)).catch(() => setCustomers([]));

  const saveReviewLink = async (customerId) => {
    if (!reviewLink.trim()) return;
    try {
      await axios.post(`${API}/customers/${customerId}/review-link`, { review_link: reviewLink.trim() }, { headers });
      toast.success("Review link saved — completed jobs will text it automatically");
      setReviewFor(null);
      setReviewLink("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save review link");
    }
  };

  const provisionLine = async (customerId) => {
    setProvisionBusy(customerId);
    try {
      const { data } = await axios.post(`${API}/customers/${customerId}/provision-number`, {}, { headers });
      if (data.already_provisioned) {
        toast.info(`Line already provisioned: ${data.tracking_number}`);
      } else {
        toast.success(`Line provisioned: ${data.tracking_number}`);
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Provisioning failed");
    } finally {
      setProvisionBusy(null);
    }
  };

  const createPortalAccess = async (customerId) => {
    if (!portalEmail.trim()) return;
    setPortalBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/customers/${customerId}/account`,
        { email: portalEmail.trim() },
        { headers }
      );
      setPortalResult({ customerId, email: data.email, password: data.password, welcomeSent: data.welcome_sent });
      setPortalEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create portal login");
    } finally {
      setPortalBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(
        `${API}/customers`,
        { ...form, contact_email: form.contact_email || null, oncall_phone: form.oncall_phone || null },
        { headers }
      );
      toast.success("Customer added");
      setForm({ company_name: "", trade: "hvac", contact_email: "", oncall_phone: "" });
      await load();
    } catch (err) {
      toast.error("Could not add customer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="customers-panel" className="grid gap-5 lg:grid-cols-2">
      <form onSubmit={add} className="rounded-xl border border-white/[0.08] bg-[#12161F] p-6" data-testid="customer-form">
        <div className="flex items-center gap-2">
          <Plus className="h-4 w-4 text-[#F25F22]" />
          <h3 className="font-display text-base font-semibold text-slate-50">Add a customer</h3>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Each customer gets their missed calls answered with their own branding. The on-call phone receives emergency escalations and qualified-lead alerts.
        </p>
        <div className="mt-5 space-y-3">
          <input
            required
            value={form.company_name}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
            data-testid="customer-name-input"
            placeholder="Company name (e.g. Apex Heating & Air)"
            className={`${inputCls} w-full`}
          />
          <select
            value={form.trade}
            onChange={(e) => setForm({ ...form, trade: e.target.value })}
            data-testid="customer-trade-select"
            className={`${inputCls} w-full`}
          >
            {["hvac", "plumbing", "electrical", "roofing", "general"].map((t) => (
              <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>
            ))}
          </select>
          <input
            type="email"
            value={form.contact_email}
            onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
            data-testid="customer-email-input"
            placeholder="Customer email (receives their weekly report)"
            className={`${inputCls} w-full`}
          />
          <input
            value={form.oncall_phone}
            onChange={(e) => setForm({ ...form, oncall_phone: e.target.value })}
            data-testid="customer-oncall-input"
            placeholder="On-call phone for emergencies (e.g. +15551234567)"
            className={`${inputCls} w-full`}
          />
          <input
            value={form.review_link}
            onChange={(e) => setForm({ ...form, review_link: e.target.value })}
            data-testid="customer-review-link-input"
            placeholder="Google review link (optional, https://…)"
            className={`${inputCls} w-full`}
          />
          <button
            type="submit"
            disabled={saving}
            data-testid="customer-add-button"
            className="flex w-full items-center justify-center gap-2 rounded-md bg-[#F25F22] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#FF7539] disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Customer"}
          </button>
        </div>
      </form>

      <div className="rounded-xl border border-white/[0.08] bg-[#12161F] p-6" data-testid="customer-list">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-[#F25F22]" />
          <h3 className="font-display text-base font-semibold text-slate-50">Active customers</h3>
        </div>
        {customers === null ? (
          <div className="flex items-center gap-2 p-8 text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : customers.length === 0 ? (
          <p className="p-8 text-sm text-slate-500">No customers yet. One is auto-created for simulations.</p>
        ) : (
          <ul className="mt-4 space-y-2.5">
            {customers.map((c) => (
              <li key={c.id} data-testid={`customer-${c.id}`} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-3">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-medium text-slate-100">{c.company_name}</span>
                  <span className="rounded-full border border-[#F25F22]/30 bg-[#F25F22]/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-[#F25F22]">
                    {c.trade || "trade"}
                  </span>
                  {c.contact_email && <span className="text-xs text-slate-500">{c.contact_email}</span>}
                  {c.oncall_phone && <span className="font-mono text-xs text-slate-500">{c.oncall_phone}</span>}
                  {c.tracking_number ? (
                    <span className="font-mono text-xs text-emerald-400" data-testid={`tracking-number-${c.id}`}>
                      {c.tracking_number}
                    </span>
                  ) : (
                    <button
                      onClick={() => provisionLine(c.id)}
                      disabled={provisionBusy === c.id}
                      data-testid={`provision-line-${c.id}`}
                      className="flex items-center gap-1.5 rounded-md border border-white/[0.12] px-3 py-1.5 text-xs font-medium text-slate-300 transition-colors hover:border-[#F25F22]/50 hover:text-slate-50 disabled:opacity-50"
                    >
                      {provisionBusy === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PhoneForwarded className="h-3.5 w-3.5" />}
                      Provision line
                    </button>
                  )}
                  {c.review_link ? (
                    <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-amber-300" title={c.review_link} data-testid={`review-on-${c.id}`}>
                      <Star className="h-3 w-3" /> reviews on
                    </span>
                  ) : (
                    <button
                      onClick={() => { setReviewFor(reviewFor === c.id ? null : c.id); setReviewLink(""); }}
                      data-testid={`review-link-toggle-${c.id}`}
                      className="flex items-center gap-1.5 rounded-md border border-white/[0.12] px-3 py-1.5 text-xs font-medium text-slate-300 transition-colors hover:border-[#F25F22]/50 hover:text-slate-50"
                    >
                      <Star className="h-3.5 w-3.5" /> Review link
                    </button>
                  )}
                  <button
                    onClick={() => setPortalFor(portalFor === c.id ? null : c.id)}
                    data-testid={`portal-access-toggle-${c.id}`}
                    className="ml-auto flex items-center gap-1.5 rounded-md border border-white/[0.12] px-3 py-1.5 text-xs font-medium text-slate-300 transition-colors hover:border-[#F25F22]/50 hover:text-slate-50"
                  >
                    <KeyRound className="h-3.5 w-3.5" /> Portal access
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete ${c.company_name}? This removes their conversations, messages, estimates, and portal login. This cannot be undone.`)) return;
                      try {
                        await axios.delete(`${API}/customers/${c.id}?force=true`, { headers });
                        toast.success(`Deleted ${c.company_name}`);
                        load();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Could not delete customer");
                      }
                    }}
                    data-testid={`customer-delete-${c.id}`}
                    className="flex items-center gap-1.5 rounded-md border border-red-500/30 px-3 py-1.5 text-xs font-medium text-red-400 transition-colors hover:bg-red-500/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
                {reviewFor === c.id && (
                  <div className="mt-3 flex items-center gap-2 border-t border-white/[0.06] pt-3">
                    <input
                      value={reviewLink}
                      onChange={(e) => setReviewLink(e.target.value)}
                      data-testid={`review-link-input-${c.id}`}
                      placeholder="Google review link (Google Business → Ask for reviews)"
                      className={`${inputCls} flex-1`}
                    />
                    <button
                      onClick={() => saveReviewLink(c.id)}
                      data-testid={`review-link-save-${c.id}`}
                      className="rounded-md bg-[#F25F22] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#FF7539]"
                    >
                      Save
                    </button>
                  </div>
                )}
                {portalFor === c.id && (
                  <div className="mt-3 border-t border-white/[0.06] pt-3">
                    {portalResult?.customerId === c.id ? (
                      <div className="rounded-md border border-emerald-400/30 bg-emerald-400/[0.06] p-3.5" data-testid={`portal-credentials-${c.id}`}>
                        <p className="text-xs font-semibold text-emerald-400">
                          Portal login created{portalResult.welcomeSent ? " — welcome email sent to the customer" : ""} — share once, the password won't be shown again:
                        </p>
                        <p className="mt-1.5 font-mono text-xs text-slate-200">Email: {portalResult.email}</p>
                        <p className="font-mono text-xs text-slate-200">Password: {portalResult.password}</p>
                        <button
                          onClick={() => { navigator.clipboard.writeText(`Backfill portal: ${window.location.origin}/portal\nEmail: ${portalResult.email}\nPassword: ${portalResult.password}`); toast.success("Copied"); }}
                          data-testid={`portal-copy-${c.id}`}
                          className="mt-2 flex items-center gap-1.5 rounded border border-white/[0.12] px-2.5 py-1 text-[11px] text-slate-300 hover:text-slate-50"
                        >
                          <Copy className="h-3 w-3" /> Copy credentials
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <input
                          type="email"
                          value={portalEmail}
                          onChange={(e) => setPortalEmail(e.target.value)}
                          data-testid={`portal-email-input-${c.id}`}
                          placeholder="Customer's login email"
                          className={`${inputCls} flex-1`}
                        />
                        <button
                          onClick={() => createPortalAccess(c.id)}
                          disabled={portalBusy}
                          data-testid={`portal-create-${c.id}`}
                          className="flex items-center gap-1.5 rounded-md bg-[#F25F22] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#FF7539] disabled:opacity-50"
                        >
                          {portalBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Create login"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
